import { useIsHydrated } from '@core/shared';
import { CampaignLists, EmailListSummary, MarketingCampaignStatus, OptionsApi } from '@core/swagger-client';
import { Button, Checkbox, Close, Confirm, Delete, Dialog, ErrorMessage, Lock, Switch, WarningOutlined } from '@core/ui';
import { Link } from '@online-webinar/ui-frontend';
import { useMutation } from '@tanstack/react-query';
import { useState } from 'react';
import { Trans, useTranslation } from 'react-i18next';

interface Props {
    webinarId: string;
    campaignId: string;
    campaignStatus: MarketingCampaignStatus;
    isPublished: boolean;
    lists: CampaignLists | undefined;
    excludeInterested: boolean;
    onExcludeInterestedChange: (value: boolean) => void;
    selectedListIds: string[];
    onToggle: (id: string) => void;
    onRemove: (id: string) => void;
    onDeleteWebinarList: (id: string) => void;
    onAddGlobalLists: (newIds: string[]) => void;
    onSave: () => void;
    isSaving: boolean;
    onChanged: () => void;
}

export const AudienceTab = ({
    webinarId,
    campaignId,
    campaignStatus,
    isPublished,
    lists,
    excludeInterested,
    onExcludeInterestedChange,
    selectedListIds,
    onToggle,
    onRemove,
    onDeleteWebinarList,
    onAddGlobalLists,
    onSave,
    isSaving,
    onChanged
}: Props) => {
    const { t } = useTranslation();
    const isHydrated = useIsHydrated();
    const isDraft = campaignStatus === MarketingCampaignStatus.Draft;

    const [deleteConfirmList, setDeleteConfirmList] = useState<EmailListSummary | null>(null);
    const [selectGlobalOpen, setSelectGlobalOpen] = useState(false);
    const [pendingGlobalIds, setPendingGlobalIds] = useState<string[]>([]);

    const [selectedMore, setSelectedMore] = useState<string[]>([]);
    const [stagedGlobalIds, setStagedGlobalIds] = useState<string[]>([]);
    const sendMoreMutation = useMutation(OptionsApi.marketingCampaigns.sendMore());

    const toggleMore = (id: string) => setSelectedMore((ids) => (ids.includes(id) ? ids.filter((x) => x !== id) : [...ids, id]));

    const addAndSend = () =>
        sendMoreMutation.mutate(
            { webinarId, id: campaignId, listIds: selectedMore },
            {
                onSuccess: () => {
                    setSelectedMore([]);
                    setStagedGlobalIds([]);
                    onChanged();
                }
            }
        );

    const togglePending = (id: string) => setPendingGlobalIds((ids) => (ids.includes(id) ? ids.filter((x) => x !== id) : [...ids, id]));

    const handleOpenSelectGlobal = () => {
        setPendingGlobalIds([]);
        setSelectGlobalOpen(true);
    };

    const handleAddSelected = () => {
        if (isDraft) {
            onAddGlobalLists(pendingGlobalIds);
        } else {
            setStagedGlobalIds((prev) => [...prev, ...pendingGlobalIds]);
            setSelectedMore((prev) => [...prev, ...pendingGlobalIds]);
        }
        setSelectGlobalOpen(false);
    };

    if (isDraft) {
        const webinarLists = lists?.webinar ?? [];
        const selectedGlobalLists = (lists?.global ?? []).filter((l) => selectedListIds.includes(l.id));
        const availableGlobalLists = (lists?.global ?? []).filter((l) => !selectedListIds.includes(l.id));

        return (
            <>
                <Switch
                    id="campaign-exclude-interested"
                    classNames="mb-6"
                    checked={excludeInterested}
                    onChange={(event) => onExcludeInterestedChange(event.target.checked)}
                    label={t('EmailLists.Send.ExcludeInterested')}
                />

                {webinarLists.length > 0 && (
                    <WebinarListGroup
                        heading={t('Campaign.Lists.Webinar')}
                        lists={webinarLists}
                        selected={selectedListIds}
                        onToggle={onToggle}
                        onDelete={setDeleteConfirmList}
                    />
                )}

                {selectedGlobalLists.length > 0 && <GlobalListGroup heading={t('Campaign.Lists.Global')} lists={selectedGlobalLists} onRemove={onRemove} />}

                <div className="flex flex-wrap gap-4 mt-8">
                    <Button data-testid="campaign-audience-save" variant="secondary" isLoading={isSaving} disabled={!isHydrated} onClick={onSave}>
                        {t('Generic.Button.Save')}
                    </Button>
                    <Link
                        data-testid="campaign-create-webinar-list-link"
                        to="/$webinarId/marketing/import"
                        params={{ webinarId }}
                        search={{ campaignId }}
                        variant="secondary-outline"
                    >
                        {t('Campaign.Lists.CreateWebinarList')}
                    </Link>
                    <Button data-testid="campaign-select-existing-list" variant="secondary-outline" onClick={handleOpenSelectGlobal}>
                        {t('Campaign.Lists.SelectExistingList')}
                    </Button>
                </div>

                <Dialog open={selectGlobalOpen} onClose={() => setSelectGlobalOpen(false)} title={t('Campaign.Lists.SelectExistingList')}>
                    {availableGlobalLists.length === 0 ? (
                        <p className="text-sm text-gray-600 mb-6">{t('Campaign.Lists.NoGlobalLists')}</p>
                    ) : (
                        <div className="flex flex-col gap-2 mb-6">
                            {availableGlobalLists.map((list) => (
                                <Checkbox
                                    key={list.id}
                                    data-testid={`campaign-global-select-${list.id}`}
                                    checked={pendingGlobalIds.includes(list.id)}
                                    onChange={() => togglePending(list.id)}
                                    label={
                                        <>
                                            {list.description} <span className="text-sm text-gray-500">({list.recipientCount})</span>
                                        </>
                                    }
                                />
                            ))}
                        </div>
                    )}
                    <div className="flex gap-4">
                        <Button variant="primary-outline" disabled={isSaving} onClick={() => setSelectGlobalOpen(false)}>
                            {t('Generic.Button.Cancel')}
                        </Button>
                        <Button variant="primary" isLoading={isSaving} disabled={!isHydrated || pendingGlobalIds.length === 0} onClick={handleAddSelected}>
                            {t('Campaign.Lists.AddSelected')}
                        </Button>
                    </div>
                </Dialog>

                <Confirm
                    open={!!deleteConfirmList}
                    onClose={() => setDeleteConfirmList(null)}
                    onConfirm={() => {
                        onDeleteWebinarList(deleteConfirmList!.id);
                        setDeleteConfirmList(null);
                    }}
                    title={t('EmailLists.Delete.Title')}
                    closeText={t('Generic.Button.Cancel')}
                    confirmText={t('EmailLists.Delete.Confirm')}
                    confirmTestId="campaign-webinar-list-delete-confirm"
                >
                    <p>{(deleteConfirmList?.sendCount ?? 0) > 0 ? t('EmailLists.Delete.Body.Used') : t('EmailLists.Delete.Body.Unused')}</p>
                </Confirm>
            </>
        );
    }

    // Sent campaign: show targeted lists read-only + option to add more
    const availableWebinar = (lists?.webinar ?? []).filter((l) => !selectedListIds.includes(l.id));
    const stagedGlobalLists = (lists?.global ?? []).filter((l) => stagedGlobalIds.includes(l.id));
    const availableGlobalForDialog = (lists?.global ?? []).filter(
        (l) => !selectedListIds.includes(l.id) && !stagedGlobalIds.includes(l.id)
    );
    // "Nothing left" only when there are no webinar lists to add, no staged globals to send, AND no global
    // lists still selectable in the dialog — otherwise the "all lists are already in this campaign" message
    // would contradict the still-offered "Select existing list" action.
    const hasNothingLeft = availableWebinar.length === 0 && stagedGlobalLists.length === 0 && availableGlobalForDialog.length === 0;

    return (
        <div>
            {sendMoreMutation.error && <ErrorMessage apiError={sendMoreMutation.error} classNames="mb-4 max-w-4xl" />}

            <ListGroupReadOnly heading={t('Campaign.Lists.Webinar')} lists={(lists?.webinar ?? []).filter((l) => selectedListIds.includes(l.id))} />
            <ListGroupReadOnly heading={t('Campaign.Lists.Global')} lists={(lists?.global ?? []).filter((l) => selectedListIds.includes(l.id))} />

            <AvailableListGroup heading={t('Campaign.Lists.Webinar')} lists={availableWebinar} selected={selectedMore} onToggle={toggleMore} />
            <AvailableListGroup heading={t('Campaign.Lists.Global')} lists={stagedGlobalLists} selected={selectedMore} onToggle={toggleMore} />

            <div className="mt-8">
                {!hasNothingLeft && <p className="mb-3 font-medium">{t('Campaign.AddLists.Intro')}</p>}
                {!hasNothingLeft && selectedMore.length === 0 && (
                    <p id="campaign-add-no-selection-hint" className="mb-3">
                        {t('Campaign.AddLists.NoSelectionHint')}
                    </p>
                )}
                {!isPublished && (
                    <p id="campaign-add-not-published-warning" className="bg-warning-light rounded-md inline-flex items-center gap-2 mb-4 ps-3 pe-4 py-2">
                        <WarningOutlined classNames="size-6 fill-warning shrink-0" />
                        {t('Campaign.Warning.NotPublished')}
                    </p>
                )}
                {hasNothingLeft && <p className="text-gray-600 mb-4">{t('Campaign.AddLists.None')}</p>}
                <div className="flex flex-wrap gap-4">
                    <Button
                        data-testid="campaign-add-lists-send"
                        variant="primary"
                        isLoading={sendMoreMutation.isPending}
                        disabled={!isHydrated}
                        aria-disabled={selectedMore.length === 0 || !isPublished}
                        aria-describedby={
                            [
                                !isPublished && 'campaign-add-not-published-warning',
                                !hasNothingLeft && selectedMore.length === 0 && 'campaign-add-no-selection-hint'
                            ]
                                .filter(Boolean)
                                .join(' ') || undefined
                        }
                        onClick={() => {
                            if (!isPublished || selectedMore.length === 0) return;
                            addAndSend();
                        }}
                    >
                        {t('Campaign.AddLists.Button')}
                    </Button>
                    <Link
                        data-testid="campaign-create-webinar-list-link"
                        to="/$webinarId/marketing/import"
                        params={{ webinarId }}
                        search={{ campaignId }}
                        variant="secondary-outline"
                    >
                        {t('Campaign.Lists.CreateWebinarList')}
                    </Link>
                    <Button data-testid="campaign-select-existing-list" variant="secondary-outline" onClick={handleOpenSelectGlobal}>
                        {t('Campaign.Lists.SelectExistingList')}
                    </Button>
                </div>
            </div>

            <Dialog open={selectGlobalOpen} onClose={() => setSelectGlobalOpen(false)} title={t('Campaign.Lists.SelectExistingList')}>
                {availableGlobalForDialog.length === 0 ? (
                    <p className="text-sm text-gray-600 mb-6">{t('Campaign.Lists.NoGlobalLists')}</p>
                ) : (
                    <div className="flex flex-col gap-2 mb-6">
                        {availableGlobalForDialog.map((list) => (
                            <Checkbox
                                key={list.id}
                                data-testid={`campaign-global-select-${list.id}`}
                                checked={pendingGlobalIds.includes(list.id)}
                                onChange={() => togglePending(list.id)}
                                label={
                                    <>
                                        {list.description} <span className="text-sm text-gray-500">({list.recipientCount})</span>
                                    </>
                                }
                            />
                        ))}
                    </div>
                )}
                <div className="flex gap-4">
                    <Button variant="primary-outline" onClick={() => setSelectGlobalOpen(false)}>
                        {t('Generic.Button.Cancel')}
                    </Button>
                    <Button variant="primary" disabled={!isHydrated || pendingGlobalIds.length === 0} onClick={handleAddSelected}>
                        {t('Campaign.Lists.AddSelected')}
                    </Button>
                </div>
            </Dialog>
        </div>
    );
};

const WebinarListGroup = ({
    heading,
    lists,
    selected,
    onToggle,
    onDelete
}: {
    heading: string;
    lists: EmailListSummary[];
    selected: string[];
    onToggle: (id: string) => void;
    onDelete: (list: EmailListSummary) => void;
}) => {
    const { t } = useTranslation();
    if (lists.length === 0) return null;
    return (
        <fieldset className="max-w-2xl mb-4">
            <legend className="font-medium mb-2">{heading}</legend>
            <div className="flex flex-col gap-2">
                {lists.map((list) => (
                    <div key={list.id} className="flex items-center gap-3">
                        <Checkbox
                            classNames="flex-1"
                            data-testid={`campaign-list-${list.id}`}
                            checked={selected.includes(list.id)}
                            onChange={() => onToggle(list.id)}
                            label={
                                <>
                                    {list.description} <span className="text-sm text-gray-500">({list.recipientCount})</span>
                                </>
                            }
                        />
                        <Button iconbtn data-testid={`campaign-list-delete-${list.id}`} onClick={() => onDelete(list)}>
                            <Delete classNames="size-6 stroke-warning" />
                            <span className="sr-only">
                                <Trans i18nKey="Campaign.Lists.DeleteWebinarList" values={{ name: list.description }} />
                            </span>
                        </Button>
                    </div>
                ))}
            </div>
        </fieldset>
    );
};

const GlobalListGroup = ({
    heading,
    lists,
    onRemove
}: {
    heading: string;
    lists: { id: string; description: string; recipientCount: number }[];
    onRemove: (id: string) => void;
}) => {
    if (lists.length === 0) return null;
    return (
        <fieldset className="max-w-2xl mb-4">
            <legend className="font-medium mb-2">{heading}</legend>
            <div className="flex flex-col gap-2">
                {lists.map((list) => (
                    <div key={list.id} className="flex items-center gap-3">
                        <span className="flex-1">
                            {list.description} <span className="text-sm text-gray-500">({list.recipientCount})</span>
                        </span>
                        <Button iconbtn data-testid={`campaign-list-remove-${list.id}`} onClick={() => onRemove(list.id)}>
                            <Close classNames="size-6 stroke-warning" />
                            <span className="sr-only">
                                <Trans i18nKey="Campaign.Lists.RemoveGlobalList" values={{ name: list.description }} />
                            </span>
                        </Button>
                    </div>
                ))}
            </div>
        </fieldset>
    );
};

const AvailableListGroup = ({
    heading,
    lists,
    selected,
    onToggle
}: {
    heading: string;
    lists: EmailListSummary[];
    selected: string[];
    onToggle: (id: string) => void;
}) => {
    if (lists.length === 0) return null;
    return (
        <fieldset className="max-w-2xl mb-4">
            <legend className="font-medium mb-2">{heading}</legend>
            <div className="flex flex-col gap-2">
                {lists.map((list) => (
                    <Checkbox
                        key={list.id}
                        data-testid={`campaign-add-list-${list.id}`}
                        checked={selected.includes(list.id)}
                        onChange={() => onToggle(list.id)}
                        label={
                            <>
                                {list.description} <span className="text-sm text-gray-500">({list.recipientCount})</span>
                            </>
                        }
                    />
                ))}
            </div>
        </fieldset>
    );
};

const ListGroupReadOnly = ({ heading, lists }: { heading: string; lists: { id: string; description: string; recipientCount: number }[] }) => {
    if (lists.length === 0) return null;
    return (
        <fieldset className="mb-4">
            <legend className="font-medium mb-2">{heading}</legend>
            <div className="flex flex-col gap-1">
                {lists.map((list) => (
                    <div key={list.id} className="flex items-center gap-1">
                        <Lock classNames="size-6 stroke-body" />
                        <span>
                            {list.description}
                            <span className="text-sm text-gray-500 ms-1">({list.recipientCount})</span>
                        </span>
                    </div>
                ))}
            </div>
        </fieldset>
    );
};
